import { faPen, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import deepEqual from 'deep-equal';
import { localeData } from 'moment';
import React, { Dispatch, useMemo, useState } from 'react';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import VoxelInput from '../../../components/voxel/Forms/VoxelInput';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import { TagGq } from '../../../graphql/projects/models/TagGq';
import { TagGroupGq } from '../../../graphql/projects/models/TagGroupGq';
import { TagInputGq } from '../../../graphql/projects/mutations/tags/useEditTagsGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import AutotagTextInput from './AutotagTextInput';

export interface EditTagButtonProps {
  showEditMenu: boolean;
  setShowEditMenu: Dispatch<React.SetStateAction<boolean>>;
  tag: TagGq;
  tagGroup: TagGroupGq;
  deleted: boolean;
  setDeleted: Dispatch<React.SetStateAction<boolean>>;
  reload: () => Promise<unknown>;
}

export const EditTagButton: React.FC<EditTagButtonProps> = ({
  showEditMenu,
  setShowEditMenu,
  tag,
  tagGroup,
  deleted,
  setDeleted,
  reload,
}) => {
  // Deleting
  const deleteMutation = projectsApiService.mutations.tags.useDeleteTags();
  const handleDelete = () => {
    setDeleted(true);
    deleteMutation({
      projectId: tagGroup.projectId,
      tagIds: [tag.id],
    }).finally(reload);
  };

  // Edit form logic
  const initialLocalData: TagInputGq = {
    autoTagTags: tag.autoTagTags ?? '',
    name: tag.name,
    id: tag.id,
  };
  const [localTagData, setLocalTagData] = useState<TagInputGq>(initialLocalData);
  const pristine = useMemo(() => {
    return deepEqual(localTagData, initialLocalData);
  }, [localeData, initialLocalData]);

  const editTagMutation = projectsApiService.mutations.tags.useEditTags();
  const [saving, setSaving] = useState(false);
  const handleSave = () => {
    setSaving(true);
    editTagMutation({ tagInputs: [localTagData], projectId: tagGroup.projectId }).finally(() => {
      setSaving(false);
      setShowEditMenu(false);
    });
  };

  /*
  const debouncedValue = useDebounceValue(localTagData, 200);
  useEffect(() => {
    setSaving(true);
    editTagMutation({ tagInputs: [localTagData], projectId: tagGroup.projectId }).finally(() =>
      setSaving(false)
    );
  }, [debouncedValue]);
  */

  return (
    <VoxelPopover placement="top" open={showEditMenu} onOpenChange={setShowEditMenu}>
      <VoxelPopoverTrigger enableActive>
        <VoxelIconButton
          size="xs"
          icon={<FontAwesomeIcon icon={faPen} size="sm" />}
          color={showEditMenu ? 'primary' : 'white'}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </VoxelPopoverTrigger>
      <VoxelPopoverContent className="w-[330px]">
        <div className="absolute right-4 top-2">
          <VoxelIconButton
            icon={<FontAwesomeIcon icon={faTimes} />}
            color="white"
            onClick={() => setShowEditMenu(false)}
          />
        </div>
        <form
          className="p-6 flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <VoxelInput
            label="Tag name"
            value={localTagData.name}
            onChange={(event) => setLocalTagData((prev) => ({ ...prev, name: event.target.value }))}
            labelPosition="top"
            autoFocus
            size="sm"
            disabled={saving}
          />
          <AutotagTextInput
            tagInput={localTagData}
            setTagInput={setLocalTagData}
            disabled={saving}
          />
          <div />
          <div className="flex flex-row justify-end gap-2">
            <VoxelButton
              title="Delete tag"
              color="error"
              onClick={handleDelete}
              size="sm"
              disabled={saving}
            />

            <VoxelButton
              title="Save changes"
              color="primary"
              onClick={handleSave}
              size="sm"
              type="submit"
              disabled={saving || pristine}
            />
          </div>
        </form>
      </VoxelPopoverContent>
    </VoxelPopover>
  );
};

export default EditTagButton;
